import { AreaCatalogue, useI18n } from "@baton/i18n";
import { Mail, X } from "@baton/icons";
import { Banner, Button, IconButton } from "@baton/ui";
import type { Runtime } from "@baton/core";
import { Suspense } from "react";
import type { MeView } from "../../app/queries.ts";
import { VERIFY_AREA } from "./catalogue.ts";
import { useEmailVerification } from "./useEmailVerification.ts";

export interface VerifyEmailBannerProps {
  readonly runtime: Runtime;
  readonly me: MeView;
  readonly onDismiss: () => void;
}

/**
 * 001 FR-153, 015 `contracts/screens.md` § Workspace shell, the unverified-address banner
 * (2026-09-28): while the signed-in person's address is not verified, an amber strip across the top
 * of the Team says so, with Send verification email and Dismiss. Amber and not the connection
 * banners' red, because nothing is broken: the address only needs proving before the intake
 * mailbox matches the person's mail. Dismissed, it stays away for the rest of the page's life; it
 * returns on the next load for as long as the address is unverified. The shell shows it only when
 * no connection banner is up, since those are about whether anything works at all. It is no live
 * region: it reports no change, and a reminder announced on every load would be noise.
 */
export function VerifyEmailBanner(props: VerifyEmailBannerProps) {
  return (
    <Suspense fallback={null}>
      <AreaCatalogue area={VERIFY_AREA}>
        <Strip {...props} />
      </AreaCatalogue>
    </Suspense>
  );
}

function Strip({ runtime, me, onDismiss }: VerifyEmailBannerProps) {
  const { t } = useI18n();
  const verification = useEmailVerification(runtime, me);
  if (!verification.unverified) return null;
  return (
    <Banner
      variant="verify-email"
      accent="amber"
      tile={<Mail aria-hidden="true" />}
      actions={
        <Button size="sm" variant="secondary" className="h-(--chip-height-md) px-2 text-text-primary" loading={verification.sending} onClick={() => void verification.send()}>
          {t("auth.verify.send")}
        </Button>
      }
      dismiss={<IconButton size="sm" variant="ghost" className="h-(--chip-height-md) text-accent-amber-on-solid" icon={<X />} label={t("auth.verify.dismiss")} onClick={onDismiss} />}
    >
      {t("auth.verify.banner")}
    </Banner>
  );
}
